import { Formik, Form } from "formik"
import { Switch, Grid, Typography, Alert } from "@mui/material"
import * as Yup from "yup"
import { makeStyles } from "tss-react/mui"

import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { FormikSubmitButton, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { Box, Stack } from "@mui/system"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { updateOrderMeetingDisplay } from "../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { ServiceCenterMeetingQuery } from "../../meetings/meetingApi"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { enqueueSnackbar } from "notistack"

export interface MeetingFormValues {
    meetingName: string
    isCanceled: boolean
    isExternal: boolean
}

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "2rem",
    },
    submitButtonContainer: {
        width: "100%",
        display: "flex",
        justifyContent: "center",
    },
    submitButton: {
        width: "fit-content",
        backgroundColor: theme.palette.tailwind.slate[800],
        padding: "0.75rem 4rem !important",
    },
    switchContainer: {
        marginTop: "2rem",
    },
    dialogPaper: {
        paddingBottom: "3rem",
    },
}))

type Props = Readonly<{
    isOpen: boolean
    orderId?: number
    meetingName?: string
    handleClose: () => void
}>

export const ManageMeetingDisplayDialog = ({ isOpen, orderId, meetingName = "", handleClose }: Props) => {
    const { classes } = useStyles()
    const initialValues: MeetingFormValues = {
        meetingName,
        isCanceled: false,
        isExternal: false,
    }
    const { l10n } = useAppLocalization()

    const { mutate: updateMeetingDisplay, isLoading } = useApiMutation(updateOrderMeetingDisplay, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterMeetingQuery.SERVICE_CENTER_MEETING_DETAILS],
            })
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString(
                    "service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-success-message",
                ),
            })
            handleClose()
        },
    })

    const overrideMeetingSchema = Yup.object({
        meetingName: Yup.string().required(
            l10n.getString(
                "service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-name-field-required-error",
            ),
        ),
        isCanceled: Yup.boolean(),
    })

    const handleSubmit = async (values: MeetingFormValues) => {
        updateMeetingDisplay({
            orderId: orderId ?? 0,
            order: {
                meetingDisplay: {
                    ...values,
                },
            },
        })
    }

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-title">
                    <>Manage meeting on displays</>
                </LocalizedStrict>
            }
            open={isOpen}
            onClose={handleClose}
            maxWidthProp="md"
            slotProps={{
                paper: {
                    className: classes.dialogPaper,
                },
            }}
        >
            <Formik<MeetingFormValues>
                initialValues={initialValues}
                validationSchema={overrideMeetingSchema}
                onSubmit={handleSubmit}
            >
                {({ values, handleChange }) => (
                    <Form>
                        <Grid container spacing={2} className={classes.container}>
                            <Grid item xs={6}>
                                <LocalizedStrict
                                    id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-meeting-name-label"
                                    attrs={{ label: true, placeholder: true }}
                                >
                                    <FormikTextField
                                        name="meetingName"
                                        label="New meeting name on displays"
                                        placeholder="Meeting name"
                                        fullWidth
                                    />
                                </LocalizedStrict>

                                <Stack className={classes.switchContainer} gap={1}>
                                    <Box display="flex" alignItems="center">
                                        <Switch name="isCanceled" checked={values.isCanceled} onChange={handleChange} />
                                        <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-switch-label">
                                            <Typography>Show as canceled</Typography>
                                        </LocalizedStrict>
                                    </Box>

                                    <Box display="flex" alignItems="center">
                                        <Switch name="isExternal" checked={values.isExternal} onChange={handleChange} />
                                        <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-show-on-screens-switch-label">
                                            <Typography>Show on screens</Typography>
                                        </LocalizedStrict>
                                    </Box>
                                </Stack>
                            </Grid>
                            <Grid item xs={6}>
                                <Box>
                                    <Alert severity="info">
                                        <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-alert-change-name-label">
                                            <Typography>Change meeting name:</Typography>
                                        </LocalizedStrict>
                                        <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-alert-change-name-description">
                                            <Typography>
                                                If the meeting name is not appropriate for display on meeting displays,
                                                you can edit it here. This modification will only affect the name shown
                                                on the displays.
                                            </Typography>
                                        </LocalizedStrict>

                                        <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-alert-cancel-meeting-label">
                                            <Typography pt={3}>Cancel meeting:</Typography>
                                        </LocalizedStrict>
                                        <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-alert-cancel-meeting-description">
                                            <Typography>
                                                If you need to cancel a meeting while keeping it visible, you can simply
                                                mark it as canceled.
                                            </Typography>
                                        </LocalizedStrict>
                                    </Alert>
                                </Box>
                            </Grid>
                        </Grid>
                        <Box className={classes.submitButtonContainer}>
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-manage-meeting-modal-submit-button">
                                <FormikSubmitButton className={classes.submitButton} variant="contained">
                                    Save
                                </FormikSubmitButton>
                            </LocalizedStrict>
                        </Box>
                    </Form>
                )}
            </Formik>
        </StandardDialog>
    )
}
